import { ReactElement, ReactNode } from "react";
import { Box, Stack, StackProps } from '@mui/material';
import { toKebabCase } from '../../utilities/display-utilities';
import ControlledTooltip from "../controlled-tooltip/ControlledTooltip";
import { Typography, TypographyVariant, BodyVariants } from "@chr/eds-react";

interface InformationDisplayProps extends StackProps {
  label: string;
  truncateText?: boolean;
  labelVariant?: Exclude<TypographyVariant, BodyVariants>;
  textVariant?: Exclude<TypographyVariant, BodyVariants>;
  labelIcon?: ReactNode;
  customTestId?: string;
}

interface InformationDisplayPropsWithComponent extends Omit<InformationDisplayProps, 'component'> {
  component: ReactElement
}

interface InformationDisplayPropsWithTextString extends InformationDisplayProps {
  text: string | number | undefined;
}

const getTestId = (label: string, type: 'label' | 'value', customTestId?: string | undefined): string => {
  const testId = customTestId ? `${toKebabCase(customTestId)}` : `${toKebabCase(label)}`;
  return `${testId}-field-${type}`
}

export const InformationDisplay = (
  props: InformationDisplayPropsWithComponent | InformationDisplayPropsWithTextString
) => {
  const {
    label,
    truncateText,
    labelVariant,
    textVariant,
    labelIcon,
    customTestId,
    component,
    text,
    ...parentProps
  } = { text: null,component: null, ...props};

  const newTextVariant = textVariant ?? 'subtitle300';
  const newLabelVariant = labelVariant ?? 'subtitle300';

  return (
    <Stack {...parentProps}>
      {label && (
        <Box display={'flex'} alignItems={'center'}>
          <Typography fontWeight={'regular'} data-testid={getTestId(label, 'label', customTestId)} variant={newLabelVariant}>
            {label}
          </Typography>
          {labelIcon}
        </Box>
      )}

      {text && (       
        <ControlledTooltip title={text}>
          <Typography fontWeight="semiBold" variant={newTextVariant} data-testid={getTestId(label, 'value', customTestId)}>
            {text}
          </Typography>
        </ControlledTooltip>
      )}

      {component && <div data-testid={getTestId(label, 'value', customTestId)}>{component}</div>}
    </Stack>
  );
};